import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, User, Calendar, Loader2 } from 'lucide-react';
import StarRating from './StarRating';

const ReviewsModal = ({ school, isOpen, onClose, refreshTrigger }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const scrollRef = useRef(null);
    const reviewsPerPage = 10;

    // Fetch reviews and stats when modal opens or refreshTrigger changes
    useEffect(() => {
        if (isOpen && school) {
            fetchReviewsAndStats();
        }
    }, [isOpen, school, refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchReviewsAndStats = async (page = 1, append = false) => {
        if (!school) return;

        try {
            if (page === 1) {
                setLoading(true);
                setError(null);
            } else {
                setLoadingMore(true);
            }

            // Fetch reviews
            const reviewsResponse = await fetch(
                `/api/reviews/school/${school.id}?limit=${reviewsPerPage}&offset=${(page - 1) * reviewsPerPage}`
            );

            if (!reviewsResponse.ok) {
                throw new Error('Failed to fetch reviews');
            }

            const reviewsData = await reviewsResponse.json();

            // Fetch stats (only on first load)
            if (page === 1) {
                const statsResponse = await fetch(`/api/reviews/stats/${school.id}`);
                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    setStats(statsData);
                }
            }

            if (append) {
                setReviews(prev => [...prev, ...reviewsData.reviews]);
            } else {
                setReviews(reviewsData.reviews);
            }

            setHasMore(reviewsData.pagination?.hasMore || false);
            setCurrentPage(page);

        } catch (err) {
            console.error('Error fetching reviews:', err);
            setError('Failed to load reviews. Please try again.');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const loadMoreReviews = () => {
        if (!loadingMore && hasMore) {
            fetchReviewsAndStats(currentPage + 1, true);
        }
    };

    const handleScroll = () => {
        if (!scrollRef.current || loadingMore || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            loadMoreReviews();
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0) || '';
        const last = lastName?.charAt(0) || '';
        return (first + last).toUpperCase();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black bg-opacity-50 modal-backdrop flex items-center justify-center z-50 p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">
                                Reviews for {school.name}
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {school.district}, {school.region}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X size={24} className="text-gray-500" />
                        </button>
                    </div>

                    {/* Stats Section */}
                    {stats && (
                        <div className="p-6 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-gray-900">
                                            {stats.averageRating.toFixed(1)}
                                        </div>
                                        <StarRating
                                            rating={stats.averageRating}
                                            size={20}
                                            showCount={true}
                                            reviewCount={stats.reviewCount}
                                        />
                                    </div>
                                    {stats.ratingDistribution && (
                                        <div className="flex-1 max-w-md">
                                            {[5, 4, 3, 2, 1].map((rating) => {
                                                const count = stats.ratingDistribution[rating] || 0;
                                                const percentage = stats.reviewCount > 0 ? (count / stats.reviewCount) * 100 : 0;
                                                return (
                                                    <div key={rating} className="flex items-center space-x-2 mb-1">
                                                        <span className="text-sm text-gray-600 w-2">{rating}</span>
                                                        <Star size={12} className="text-yellow-400 fill-current" />
                                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                            <div
                                                                className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                                                                style={{ width: `${percentage}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-sm text-gray-600 w-8">{count}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reviews List */}
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto max-h-96 reviews-scrollbar"
                        onScroll={handleScroll}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center p-8">
                                <Loader2 size={32} className="loading-spinner text-primary-600" />
                                <span className="ml-2 text-gray-600">Loading reviews...</span>
                            </div>
                        ) : error ? (
                            <div className="p-8 text-center">
                                <p className="text-red-600">{error}</p>
                                <button
                                    onClick={() => fetchReviewsAndStats(1)}
                                    className="mt-4 px-4 py-2 btn-review text-white rounded-lg"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : reviews.length === 0 ? (
                            <div className="p-8 text-center">
                                <Star size={48} className="text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-600 text-lg">No reviews yet</p>
                                <p className="text-gray-500">Be the first to review this school!</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {reviews.map((review, index) => (
                                    <motion.div
                                        key={review.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="p-6 hover:bg-gray-50 transition-colors review-card"
                                    >
                                        <div className="flex items-start space-x-4">
                                            {/* Avatar */}
                                            <div className="flex-shrink-0">
                                                <div className="w-10 h-10 review-avatar rounded-full flex items-center justify-center">
                                                    {review.firstName || review.lastName ? (
                                                        <span className="text-sm font-medium">
                                                            {getInitials(review.firstName, review.lastName)}
                                                        </span>
                                                    ) : (
                                                        <User size={20} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Review Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <div className="flex items-center space-x-1">
                                                        {review.firstName && review.lastName ? (
                                                            <span className="font-medium text-gray-900">
                                                                {review.firstName} {review.lastName}
                                                            </span>
                                                        ) : (
                                                            <span className="font-medium text-gray-900">
                                                                Anonymous
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-gray-400">•</span>
                                                    <div className="flex items-center space-x-1 text-gray-500">
                                                        <Calendar size={14} />
                                                        <span className="text-sm">
                                                            {formatDate(review.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="mb-3">
                                                    <StarRating
                                                        rating={review.rating}
                                                        size={16}
                                                        showNumber={false}
                                                    />
                                                </div>

                                                {review.comment && (
                                                    <p className="text-gray-700 leading-relaxed review-text">
                                                        {review.comment}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}

                                {/* Load More Indicator */}
                                {loadingMore && (
                                    <div className="flex items-center justify-center p-4">
                                        <Loader2 size={20} className="loading-spinner text-primary-600" />
                                        <span className="ml-2 text-gray-600">Loading more reviews...</span>
                                    </div>
                                )}

                                {!hasMore && reviews.length > 0 && (
                                    <div className="p-4 text-center text-gray-500">
                                        <p>You've reached the end of the reviews</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                Showing {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
                                {stats && ` of ${stats.reviewCount} total`}
                            </p>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 btn-review text-white rounded-lg"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ReviewsModal;
